import { useEffect, useRef, useState } from 'react';

import { centsToCurrency, currencyToCents } from '@/util/number';
import { CalculateCost } from '@/util/payments';
import { trackOrderPayRateInteracted } from '@/util/vexo';

import { JobDraftV2 } from '../../context';
import useRateRange from '../../hooks/useRateRange';
import { calculateWallClockDurationMinutes } from '../../hooks/useDurationCalculations';
import { BillingAccountV2, SkillListItem } from '../../api/types';

import {
  CardTopRow,
  JobList,
  MutedNote,
  PageShell,
  RateCard,
  RateInput,
  RateInputDollar,
  RateInputShell,
  RateLabel,
  RateRow,
  RatingDot,
  RatingRow,
  SkillTitle,
  StepHeading,
  StepSubheading,
  TotalHoursLabel,
} from './styles';

type Tone = 'bad' | 'avg' | 'good' | 'neutral';

type RateRange = { minimum: number; maximum: number } | null;

type PayRateStepProps = {
  jobs: JobDraftV2[];
  onUpdateJobs: (jobs: JobDraftV2[]) => void;
  skills?: SkillListItem[];
  billingAccount: BillingAccountV2 | null;
  onValidityChange?: (valid: boolean) => void;
};

// Per-shift-quantity-weighted total hours using wall-clock duration.
// See the matching helper in ReviewStep for the rationale (8 hrs × 1 worker +
// 8 hrs × 3 workers must total 32 hrs, not 16 × job.quantity). Wall-clock
// duration ignores DST so admin always sees what they scheduled (11 PM to 3 AM
// is always 4 hours, not 3 on spring-forward). This matches ScheduleStep display.
const totalShiftHours = (job: JobDraftV2) => {
  return (job.shifts || []).reduce((sum, shift) => {
    const startTime = shift.start_at.slice(11, 16);
    const endTime = shift.end_at.slice(11, 16);
    // Convert 24h format to 12h for the utility function
    const start12h = (() => {
      const [h, m] = startTime.split(':').map(Number);
      const ap = h < 12 ? 'AM' : 'PM';
      const h12 = h % 12 || 12;
      return `${h12}:${String(m).padStart(2, '0')} ${ap}`;
    })();
    const end12h = (() => {
      const [h, m] = endTime.split(':').map(Number);
      const ap = h < 12 ? 'AM' : 'PM';
      const h12 = h % 12 || 12;
      return `${h12}:${String(m).padStart(2, '0')} ${ap}`;
    })();
    const wallClockMins = calculateWallClockDurationMinutes(start12h, end12h);
    const hours = wallClockMins / 60 - (shift.break_minutes || 0) / 60;
    const qty = shift.quantity ?? job.quantity ?? 1;
    return sum + Math.max(hours, 0) * qty;
  }, 0);
};

const toneFromValue = (cents: number, range: RateRange): Tone => {
  if (!range || range.maximum <= 0) return 'neutral';
  if (cents <= 0) return 'neutral';
  if (cents < range.minimum) return 'bad';
  if (cents < range.maximum) return 'avg';
  return 'good';
};

const messageForTone = (tone: Tone) => {
  switch (tone) {
    case 'bad':
      return 'Raise the rate to meet minimum wage.';
    case 'avg':
      return 'Consider an increase to attract candidates.';
    case 'good':
      return 'Great — this is a competitive rate!';
    default:
      return 'Enter a pay rate to see how it compares to the local market.';
  }
};

type JobRateCardProps = {
  job: JobDraftV2;
  index: number;
  skills?: SkillListItem[];
  markupPercent: number;
  range: RateRange;
  onUpdateJob: (next: JobDraftV2) => void;
};

const JobRateCard = ({
  job,
  index,
  skills,
  markupPercent,
  range,
  onUpdateJob,
}: JobRateCardProps) => {
  const skill = skills?.find((s) => s.id === job.skill_id) || null;
  const skillName =
    skill?.display_name_en || skill?.name || `Shift ${index + 1}`;
  // totalShiftHours is already quantity-weighted per shift — do not
  // multiply by job.quantity again or 8×1 + 8×3 would render as 48
  // instead of 32 on the rate card.
  const hours = totalShiftHours(job);

  // The input is the source of truth while the user is typing. We only seed
  // it once from job.pay_rate so re-commits from onChange don't snap a
  // half-typed value like "21" up to "21.00".
  const [payInput, setPayInput] = useState<string>(() =>
    job.pay_rate ? centsToCurrency(job.pay_rate) : '',
  );
  // Vexo `order_pay_rate_interacted` — `lastTrackedDollarsRef` seeds with the
  // initial value so a first blur that didn't actually change anything
  // (tab-through of an unedited pre-filled input, OR tab-through of an empty
  // input) is suppressed.
  const lastTrackedDollarsRef = useRef<number | null>(
    job.pay_rate ? job.pay_rate / 100 : 0,
  );

  // Markup pricing is job-scoped (V1 parity). The cards are keyed by
  // array index, so when `jobs` is reordered — Remove an earlier skill,
  // "Add Skills" prepending a fresh draft, or the Review-step "Edit"
  // move-to-front — the JobRateCard instance for a given index is reused
  // while its `job` prop becomes a *different* skill. Without a resync,
  // `payInput` keeps the previous skill's rate, displaying (and on
  // commit, copying) one skill's pay rate onto another repeated skill.
  //
  // We only adopt `job.pay_rate` when it disagrees with what's already
  // typed (compared in cents). This is what lets the resync coexist with
  // the live commit-on-keystroke model below: the user typing "21"
  // commits 2100, `job.pay_rate` becomes 2100, the cents match, and we
  // do NOT snap the field to "21.00". A reorder swaps in a job whose
  // rate genuinely differs, so the cents diverge and the field adopts
  // the correct per-skill value.
  useEffect(() => {
    const inputCents = Math.round(
      (Number(payInput.replace(/[$,]/g, '')) || 0) * 100,
    );
    if (inputCents === (job.pay_rate || 0)) return;
    setPayInput(job.pay_rate ? centsToCurrency(job.pay_rate) : '');
  }, [job.pay_rate, payInput]);

  const payValueCents = Math.round(
    (Number(payInput.replace(/[$,]/g, '')) || 0) * 100,
  );
  const tone = toneFromValue(payValueCents, range);

  const commitPay = (raw: string) => {
    const trimmed = raw.trim();
    const cents = trimmed ? Math.round(currencyToCents(trimmed)) : 0;
    if (Number.isNaN(cents)) return;
    const cost = CalculateCost(trimmed || '0', markupPercent ?? 0);
    // V1 parity: keep `original_*` aligned with the markup-derived pair
    // on every edit. The backend re-validates markup jobs against
    // `original_pay_rate`/`original_cost_rate`; stale/0 values here are
    // what 422s with "Invalid markup rates".
    onUpdateJob({
      ...job,
      pay_rate: cents,
      cost_rate: cost,
      original_pay_rate: cents,
      original_cost_rate: cost,
      pay_rate_touched: true,
    });
  };

  // Strip everything but digits and a single decimal point so users can't
  // type letters or other separators. Anything that parses to a finite
  // number is committed live so the wizard's Continue button enables
  // without waiting for blur.
  const handlePayInputChange = (raw: string) => {
    const digitsAndDot = raw.replace(/[^0-9.]/g, '');
    const firstDot = digitsAndDot.indexOf('.');
    const sanitized =
      firstDot === -1
        ? digitsAndDot
        : digitsAndDot.slice(0, firstDot + 1) +
          digitsAndDot.slice(firstDot + 1).replace(/\./g, '');
    setPayInput(sanitized);
    if (sanitized === '' || sanitized === '.') {
      // Zeroed rates would otherwise read as "never quoted" and let the
      // cached rate quote be replayed straight back into the field the user
      // just emptied (GRAVY-10445) — `pay_rate_touched` is what says
      // "cleared on purpose".
      onUpdateJob({
        ...job,
        pay_rate: 0,
        cost_rate: 0,
        original_pay_rate: 0,
        original_cost_rate: 0,
        pay_rate_touched: true,
      });
      return;
    }
    if (Number.isFinite(Number(sanitized))) {
      commitPay(sanitized);
    }
  };

  const rangeLabel = range
    ? `Market range: $${(range.minimum / 100).toFixed(2)} – $${(range.maximum / 100).toFixed(2)}`
    : null;

  // Show an explicit below-minimum error alongside the existing tone
  // colors so users understand *why* the wizard's Continue button is
  // disabled (v1 parity with CustomErrorsForm). Only emitted when a
  // minimum is actually available; an in-flight or failed range fetch
  // continues to fall through silently.
  const belowMin =
    !!range &&
    range.minimum > 0 &&
    payValueCents > 0 &&
    payValueCents < range.minimum;

  return (
    <RateCard>
      <CardTopRow>
        <SkillTitle>Set Pay Rate for {skillName}</SkillTitle>
        <TotalHoursLabel>
          Total Hours: <strong>{hours.toFixed(2)}</strong>
        </TotalHoursLabel>
      </CardTopRow>

      <RateRow>
        <RateLabel>Pay Rate</RateLabel>
        <RateInputShell data-tour="orderflow-pay-rate-input">
          <RateInputDollar>$</RateInputDollar>
          <RateInput
            type="text"
            inputMode="decimal"
            placeholder="0.00"
            value={payInput}
            style={{
              color:
                tone === 'bad'
                  ? '#DC1515'
                  : tone === 'avg'
                    ? '#EC7F00'
                    : tone === 'good'
                      ? '#45A735'
                      : '#1C1C1C',
            }}
            onChange={(event) => handlePayInputChange(event.target.value)}
            onBlur={() => {
              commitPay(payInput);
              const dollars =
                Number((payInput || '0').replace(/[$,]/g, '')) || 0;
              if (lastTrackedDollarsRef.current === dollars) return;
              lastTrackedDollarsRef.current = dollars;
              const suggested =
                range && range.minimum > 0 && range.maximum > 0
                  ? (range.minimum + range.maximum) / 2 / 100
                  : null;
              trackOrderPayRateInteracted({
                rate_entered: dollars,
                suggested_rate: suggested,
                delta_from_suggestion:
                  suggested != null ? dollars - suggested : null,
                input_method: 'typed',
              });
            }}
          />
        </RateInputShell>
      </RateRow>

      <RatingRow tone={tone} data-tour="orderflow-pay-rate-feedback">
        <RatingDot tone={tone} />
        <span>{messageForTone(tone)}</span>
      </RatingRow>

      {belowMin && range ? (
        <div
          role="alert"
          style={{
            marginTop: 8,
            padding: '8px 12px',
            borderRadius: 6,
            background: '#FDECEC',
            border: '1px solid #F5B7B7',
            color: '#A41B1B',
            fontSize: 13,
            lineHeight: 1.4,
          }}
        >
          <strong>Pay rate is below minimum</strong> — the minimum pay rate is $
          {(range.minimum / 100).toFixed(2)}.
        </div>
      ) : null}

      {rangeLabel ? (
        <MutedNote data-tour="orderflow-pay-rate-range">{rangeLabel}</MutedNote>
      ) : null}
    </RateCard>
  );
};

const PayRateStep = ({
  jobs,
  onUpdateJobs,
  skills,
  billingAccount,
  onValidityChange,
}: PayRateStepProps) => {
  // The wizard walks one skill at a time — every other step
  // (JobType/Schedule/Staffing/Instructions) reads/writes jobs[0], the
  // "active" skill. The markup Pay Rate step mirrors that: it shows only
  // the active skill's rate, not a list of every skill. Each repeated
  // skill gets its own rate when it becomes the active job — the initial
  // walk-through, or the Review-step "Edit" which move-to-fronts that
  // skill into jobs[0]. Per-skill rate enforcement before submit still
  // happens on the Review step (its JobCard gates every job's rate).
  const activeJob = jobs.length > 0 ? jobs[0] : null;

  // Shared cached fetch — see hooks/useRateRange.ts. Hoisted out of the
  // JobRateCard so the validity gate reads `isSettled` directly from the
  // query result instead of an intermediate child→parent setState dance.
  // That intermediate dance had a fatal cache-hot race: with a warm
  // cache (Schedule had already fetched the same tuple), the child's
  // mount effect queued `setRangeSettled(true)` and the parent's reset
  // effect queued `setRangeSettled(false)`; React 18 batched them, the
  // net state matched the initial, no re-render fired, and `rangeSettled`
  // stayed false — leaving Continue permanently disabled on a fresh
  // markup order whose prefilled rate already cleared minimum.
  const { range: fetchedRange, isSettled: rangeSettled } = useRateRange(
    billingAccount?.id ?? null,
    activeJob?.skill_id ?? null,
    activeJob?.address_id ?? null,
  );
  const range: RateRange = fetchedRange ?? null;

  const updateActiveJob = (next: JobDraftV2) => {
    if (jobs.length === 0) return;
    const copy = [...jobs];
    copy[0] = next;
    onUpdateJobs(copy);
  };

  // Continue is gated on the active skill's pay rate meeting its
  // minimum. Other skills are validated when they are walked through the
  // wizard in turn (and again, collectively, on the Review step).
  //
  // While the rate-range fetch is still in flight (`!rangeSettled`) we
  // report invalid so a stale template-seeded pay can't be Continue-d
  // past before the minimum is known. Once settled, a successful range
  // gates on `pay >= minimum`; a null/failed range falls through to
  // `pay > 0` so a backend hiccup never permanently blocks placement.
  useEffect(() => {
    if (!onValidityChange) return;
    if (!activeJob) {
      onValidityChange(true);
      return;
    }
    if (!rangeSettled) {
      onValidityChange(false);
      return;
    }
    const valid =
      !range || range.minimum <= 0
        ? (activeJob.pay_rate || 0) > 0
        : (activeJob.pay_rate || 0) >= range.minimum;
    onValidityChange(valid);
  }, [activeJob, range, rangeSettled, onValidityChange]);

  if (!billingAccount) {
    return (
      <PageShell>
        <StepHeading>Set Pay Rate</StepHeading>
        <StepSubheading>
          Select a billing account to configure pay rates.
        </StepSubheading>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <StepHeading>Set Pay Rate</StepHeading>
      <StepSubheading>
        Choose a pay rate for this skill. Bill rate is calculated automatically
        from the markup percent on this billing account.
      </StepSubheading>

      <JobList>
        {activeJob ? (
          <JobRateCard
            key={0}
            job={activeJob}
            index={0}
            skills={skills}
            markupPercent={billingAccount.markup_percent ?? 0}
            range={range}
            onUpdateJob={updateActiveJob}
          />
        ) : null}
      </JobList>
    </PageShell>
  );
};

export default PayRateStep;
